import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { ReportRun } from '../../data/types';
import { computeHoldings } from '../../domain/holdings';
import { date, nowStamp } from '../../lib/format';
import { newId } from '../../lib/random';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';

const QUARTERS = [
  { end: '2026-09-30', label: 'September quarter 2026' },
  { end: '2026-06-30', label: 'June quarter 2026' },
];

export default function ReportRunForm({ onDone }: { onDone: () => void }) {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const [quarterEnd, setQuarterEnd] = useState(QUARTERS[0].end);
  const quarterLabel = QUARTERS.find((item) => item.end === quarterEnd)!.label;
  const [title, setTitle] = useState(`Fides ${QUARTERS[0].label} report`);
  const [reportFile, setReportFile] = useState('Fides Quarterly Report Sep 2026.pdf');
  const [message, setMessage] = useState(
    'Thank you for investing with us. This quarter’s report covers each fund’s loans and projects, and your statement shows what you have invested and received so far.',
  );
  const [delivery, setDelivery] = useState<ReportRun['delivery']>('APP_AND_EMAIL');
  const [includeStatement, setIncludeStatement] = useState(true);
  const holdings = useMemo(
    () => computeHoldings(state.ledger, state.classes, state.commitments, quarterEnd),
    [state.ledger, state.classes, state.commitments, quarterEnd],
  );
  const fundsWithHolders = scope.funds.filter((fund) =>
    holdings.some((holding) => holding.fundId === fund.id),
  );
  const [fundIds, setFundIds] = useState<string[]>(() => fundsWithHolders.map((fund) => fund.id));
  const holderIds = Array.from(
    new Set(
      holdings
        .filter((holding) => fundIds.includes(holding.fundId))
        .map((holding) => holding.holderId),
    ),
  ).filter((id) => scope.holderIds.has(id));
  const post = holderIds.filter((id) => scope.holderById.get(id)?.communication === 'POST').length;
  const sample = scope.holderById.get(holderIds[0] ?? '');
  const firstName = sample?.contactName.split(' ')[0] ?? 'Investor';

  const submit = () => {
    dispatch({
      type: 'REPORT_RUN_SUBMIT',
      run: {
        id: newId('rp'),
        tenantId: scope.tenant.id,
        title,
        quarterEnd,
        fundIds,
        reportFile,
        message,
        delivery,
        includeStatement,
        holderIds,
        status: 'PENDING_APPROVAL',
        createdBy: scope.user.name,
        createdAt: nowStamp(),
      },
    });
    toast('Report sent to Approvals');
    onDone();
  };

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <TextField
            select
            label="Quarter"
            value={quarterEnd}
            onChange={(event) => {
              const next = QUARTERS.find((item) => item.end === event.target.value)!;
              setQuarterEnd(next.end);
              setTitle(`Fides ${next.label} report`);
            }}
          >
            {QUARTERS.map((quarter) => (
              <MenuItem key={quarter.end} value={quarter.end}>
                {quarter.label}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Funds
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Investors are everyone holding units in these funds on {date(quarterEnd)}. No lists to
              keep up to date.
            </Typography>
            <FormGroup row>
              {fundsWithHolders.map((fund) => (
                <FormControlLabel
                  key={fund.id}
                  control={
                    <Checkbox
                      checked={fundIds.includes(fund.id)}
                      onChange={(event) =>
                        setFundIds((current) =>
                          event.target.checked
                            ? [...current, fund.id]
                            : current.filter((id) => id !== fund.id),
                        )
                      }
                    />
                  }
                  label={fund.code}
                />
              ))}
            </FormGroup>
          </Box>
          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <TextField
            label="Quarterly report (PDF)"
            value={reportFile}
            onChange={(event) => setReportFile(event.target.value)}
            helperText="The one report Fides writes for the quarter; everyone receives it."
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeStatement}
                onChange={(event) => setIncludeStatement(event.target.checked)}
              />
            }
            label="Add a personal statement for each investor (generated from the register)"
          />
          <TextField
            label="Cover message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            multiline
            minRows={3}
          />
          <TextField
            select
            label="Delivery"
            value={delivery}
            onChange={(event) => setDelivery(event.target.value as ReportRun['delivery'])}
            helperText={post ? `${post} investors who prefer post get a printed copy.` : undefined}
          >
            <MenuItem value="APP_AND_EMAIL">Email and the investor app</MenuItem>
            <MenuItem value="APP_ONLY">Investor app only (notification, no email)</MenuItem>
          </TextField>
          <Box>
            <Button
              variant="contained"
              disabled={!holderIds.length || !reportFile.trim()}
              onClick={submit}
            >
              Submit {holderIds.length} investors for approval
            </Button>
          </Box>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#F8FAFC' }}>
        <Typography variant="caption" color="text.secondary">
          What {sample?.name ?? 'an investor'} receives
        </Typography>
        <Box
          sx={{
            bgcolor: '#fff',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
            mt: 1,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: scope.tenant.accent, letterSpacing: '0.15em' }}>
            FIDES
          </Typography>
          <Typography variant="h6" sx={{ mt: 1.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>
            {`Dear ${firstName},\n\n${message}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
            Attached
          </Typography>
          <Typography variant="body2">• {reportFile}</Typography>
          {includeStatement && (
            <Typography variant="body2">
              • Your investor statement as at {date(quarterEnd)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
            {quarterLabel} · also in Documents in the Fides app
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
